import React, { useState, useRef } from 'react';
import { X, Plus, Pencil, Pin, Trash2 } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';

export const TabsBar: React.FC = () => {
  const { sessions, currentSession, selectSession, deleteSession, updateSessionTitle, createSession, pinSession, reorderSessions, clearMessagesAndHistory } = useAppStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>('');
  const dragIdRef = useRef<string | null>(null);
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const { t, language } = useTranslation();
  // STORY-299: chats waiting on an answer. A pending tab stays selectable.
  const pendingBySession = useAppStore((state) => state.pendingBySession);

  const startEdit = (id: string, title: string) => {
    setEditingId(id);
    setDraft(title);
  };

  const commitEdit = () => {
    if (editingId) {
      updateSessionTitle(editingId, draft.trim() || t.chat.newChat);
      setEditingId(null);
    }
  };

  return (
    <div className="flex items-center h-16 min-w-0 px-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Tabs — scroll within a bounded width so + / user stay visible */}
      <div
        className="flex items-center space-x-1 flex-1 min-w-0 overflow-x-auto chat-scroll"
        onWheel={(e) => {
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          e.currentTarget.scrollLeft += e.deltaY;
        }}
      >
        {sessions.map((s) => {
          const active = currentSession?.id === s.id;
          const pending = Boolean(pendingBySession[s.id]);
          const hint = language === 'ru' ? 'двойной клик для переименования, правый клик для меню' : 'double-click to rename, right-click for menu';
          return (
            <div
              key={s.id}
              className={clsx(
                'tab-file group flex items-center flex-shrink-0 max-w-xs px-3 h-8 rounded-t-md cursor-grab active:cursor-grabbing select-none border border-b-0',
                active
                  ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600'
                  : 'bg-gray-200/70 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
              )}
              onClick={() => selectSession(s.id)}
              onDoubleClick={() => startEdit(s.id, s.title)}
              onMouseDown={(e) => {
                if (e.button === 1) { // middle-click closes tab
                  e.preventDefault();
                  deleteSession(s.id);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ id: s.id, x: e.clientX, y: e.clientY });
              }}
              draggable
              onDragStart={() => { dragIdRef.current = s.id; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragIdRef.current && dragIdRef.current !== s.id) reorderSessions(dragIdRef.current, s.id); }}
              title={pending ? `${s.title} (${t.chat.tabPending}) — ${hint}` : `${s.title} — ${hint}`}
              aria-busy={pending || undefined}
              data-session-id={s.id}
            >
              {editingId === s.id ? (
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit();
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  autoFocus
                  className="bg-transparent outline-none text-sm truncate max-w-[10rem]"
                />
              ) : (
                <span className="text-sm truncate max-w-[10rem]">{s.title}</span>
              )}
              {pending && (
                <>
                  <span
                    className="ml-2 h-3 w-3 flex-shrink-0 border-2 border-gray-300 dark:border-gray-500 border-t-blue-500 rounded-full animate-spin"
                    aria-hidden="true"
                  />
                  <span className="sr-only">{t.chat.tabPending}</span>
                </>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); pinSession(s.id, !s.pinned); }}
                className={clsx('ml-2 opacity-0 group-hover:opacity-100', s.pinned ? 'text-blue-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200')}
                title={s.pinned ? t.chat.unpinSession : t.chat.pinSession}
              >
                <Pin className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); startEdit(s.id, s.title); }}
                className="ml-2 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                title={t.chat.renameSession}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                className="ml-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500"
                title={t.common.close}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
      {/* New tab — outside the scroll strip */}
      <button
        onClick={() => createSession()}
        className="ml-2 flex-shrink-0 p-1.5 rounded text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
        title={t.chat.newChat}
      >
        <Plus className="h-4 w-4" />
      </button>

      {/* Context menu */}
      {menu && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow text-sm"
          style={{ left: menu.x, top: menu.y }}
          onMouseLeave={() => setMenu(null)}
        >
          <button
            className="flex items-center w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-600 dark:text-red-400"
            onClick={() => {
              // Clear chat messages and server-side history
              const id = menu.id;
              setMenu(null);
              if (currentSession?.id === id) {
                clearMessagesAndHistory();
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            {t.settings.language === 'ru' ? 'Очистить чат' : 'Clear chat'}
          </button>
          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          <button
            className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              // Close others
              const id = menu.id;
              setMenu(null);
              const keep = sessions.find(s => s.id === id);
              if (!keep) return;
              sessions.filter(s => s.id !== id).forEach(s => deleteSession(s.id));
            }}
          >{t.settings.language === 'ru' ? 'Закрыть другие' : 'Close others'}</button>
          <button
            className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              // Close tabs to the right
              const id = menu.id;
              setMenu(null);
              const index = sessions.findIndex(s => s.id === id);
              if (index === -1) return;
              sessions.slice(index + 1).forEach(s => deleteSession(s.id));
            }}
          >{t.settings.language === 'ru' ? 'Закрыть справа' : 'Close to the right'}</button>
        </div>
      )}
    </div>
  );
};


